<template>
  <div
    class="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-opacity-20"
    :class="customer.is_active ? 'tw-border-red-400' : 'tw-border-green-400'"
  >
    <div class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-space-y-0 tw-pb-2 tw-p-6">
      <div
        class="tw-flex tw-items-center tw-gap-2"
        :class="customer.is_active ? 'tw-text-red-600' : 'tw-text-green-600'"
      >
        <AlertTriangle class="tw-w-5 tw-h-5 tw-flex-shrink-0" />
        <h3 class="tw-text-lg tw-font-semibold tw-leading-none tw-m-0">
          {{
            customer.is_active
              ? t('customer_management.titles.danger_zone')
              : t('customer_management.titles.unarchive_customer')
          }}
        </h3>
      </div>
    </div>

    <div class="tw-px-6 tw-pb-6">
      <div
        class="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border tw-border-opacity-20 tw-rounded-lg tw-bg-opacity-50"
        :class="
          customer.is_active
            ? 'tw-border-red-200 tw-bg-red-50'
            : 'tw-border-green-200 tw-bg-green-50'
        "
      >
        <div>
          <h4 class="tw-font-medium">
            {{
              !customer.is_active
                ? t('customer_management.titles.unarchive_customer')
                : t('customer_management.titles.archive_customer')
            }}
          </h4>
          <p class="tw-text-sm tw-text-gray-500 tw-mt-1">
            {{
              !customer.is_active
                ? t('customer_management.forms.unarchive_customer_description')
                : t('customer_management.forms.archive_customer_description')
            }}
          </p>
        </div>

        <Dialog v-model:open="isConfirmOpen">
          <DialogTrigger as-child>
            <Button
              :variant="!customer.is_active ? 'default' : 'destructive'"
              class="tw-gap-2 tw-ml-4"
              :class="
                !customer.is_active ? 'tw-bg-green-600 hover:tw-bg-green-700 tw-text-white' : ''
              "
            >
              <RotateCcw v-if="!customer.is_active" class="tw-w-4 tw-h-4" />
              <Archive v-else class="tw-w-4 tw-h-4" />
              {{
                !customer.is_active
                  ? t('customer_management.actions.unarchive')
                  : t('customer_management.actions.archive')
              }}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {{
                  !customer.is_active
                    ? t('customer_management.titles.unarchive_customer')
                    : t('customer_management.titles.archive_customer')
                }}
              </DialogTitle>
              <DialogDescription>
                <template v-if="!customer.is_active">
                  {{ t('customer_management.forms.unarchive_confirmation_message') }}
                </template>
                <template v-else>
                  {{ t('customer_management.forms.archive_confirmation_message') }}
                </template>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" @click="isConfirmOpen = false">
                {{ t('common.forms.cancel') }}
              </Button>
              <Button
                :variant="!customer.is_active ? 'default' : 'destructive'"
                @click="handleArchiveToggle"
                :disabled="isLoading"
                :class="
                  !customer.is_active ? 'tw-bg-green-600 hover:tw-bg-green-700 tw-text-white' : ''
                "
              >
                <Loader2 v-if="isLoading" class="tw-w-4 tw-h-4 tw-mr-2 tw-animate-spin" />
                {{
                  !customer.is_active
                    ? t('customer_management.actions.unarchive')
                    : t('customer_management.actions.archive')
                }}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import { AlertTriangle, Archive, RotateCcw } from 'lucide-vue-next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-vue-next';
import { useCustomerStore } from '@/stores/customer.store';

interface Customer {
  id: number;
  name?: string;
  is_archived?: boolean;
  is_active?: boolean;
}

interface Props {
  customer: Customer;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  archiveSuccess: [];
}>();

const { t } = useI18n();
const toast = useToast();
const {
  updateActiveStatus,
  getCustomerById,
  setCurrentSelectedCustomer,
  getCurrentSelectedCustomer,
} = useCustomerStore();

const isConfirmOpen = ref(false);
const isLoading = ref(false);

const handleArchiveToggle = async () => {
  try {
    isLoading.value = true;
    const currentActiveStatus = props.customer.is_active;
    const newActiveStatus = !currentActiveStatus;

    if (!props.customer.id || isNaN(props.customer.id)) {
      throw new Error(`Customer ID is invalid: ${props.customer.id}`);
    }

    await updateActiveStatus(props.customer.id, newActiveStatus);

    // Update the current selected customer if it's the same one
    if (props.customer.id === getCurrentSelectedCustomer()?.id) {
      const updatedCustomer = await getCustomerById(props.customer.id!);
      setCurrentSelectedCustomer(updatedCustomer);
    }

    toast.open({
      message: !currentActiveStatus
        ? t('customer_management.titles.customer_unarchived_successfully')
        : t('customer_management.titles.customer_archived_successfully'),
      type: 'success',
    });

    emit('archiveSuccess');
    isConfirmOpen.value = false;
  } catch (error: any) {
    toast.open({
      message: error.response?.data?.message || t('common.errors.something_went_wrong'),
      type: 'error',
    });
  } finally {
    isLoading.value = false;
  }
};
</script>
